import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from "react-router-dom";
import { 
  format, 
  addDays,
  differenceInMonths, 
  differenceInYears,
} from "date-fns";
import toast from 'react-hot-toast';
import { saveAs } from "file-saver";
import { 
  useMediaQuery, 
  useTheme, 
  Box, 
  Typography,    
  CircularProgress, 
  Tooltip, 
  IconButton,
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Update,
  Add,
  Warning,
  IosShare,
  GroupAdd,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
  fetchGroupedLegMembersByCrewCode,
  fetchGroupedRosterDetailsByCrewCode, 
} from 'state/api';
import { 
  sortMultipleFields, 
  parseUTCDate,
} from 'state/helpers';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import NumberControl from 'components/NumberControl';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DataGridComponent from 'components/DataGridComponent';
import CheckResultDialog from 'components/CheckResultDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import LegMembersDialog from 'components/LegMembersDialog';
import DependentsDialog from './DependentsDialog';
import CrewFilterDialog from './CrewFilterDialog';
import CrewMemberList from "./CrewMemberList";


const CrewMembers = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const crewRanksData = useSelector((state) => state.crewRanks);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "CrewMembers", "create", false);
  const canView = hasRight(permissionMap, "CrewMembers", "view", false);
  const canEdit = hasRight(permissionMap, "CrewMembers", "update", false);      
  const canCheck = hasRight(permissionMap, "CrewMembers", "check", false);
  const canDependents = hasRight(permissionMap, "CrewMembers", "dependents", false);
  const canUpdateRank = hasRight(permissionMap, "CrewMembers", "rank", false);
  const canExport = hasRight(permissionMap, "CrewMembers", "export", false);
  
  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [checksList, setChecksList] = useState([]);
  const [showCheckResultDialog, setShowCheckResultDialog] = useState(false);
  const [openDependentsDialog, setOpenDependentsDialog] = useState(false);
  const [selectedCrew, setSelectedCrew] = useState(null);

  const [openLegMembersDialog, setOpenLegMembersDialog] = useState(false);
  const [openRosterDetailsDialog, setOpenRosterDetailsDialog] = useState(false);
  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  const [selectedCrewRosters, setSelectedCrewRosters] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  
  const [prevQuantity, setPrevQuantity] = useState(-1);
  const [postQuantity, setPostQuantity] = useState(1);
  const [crewScheduleCode, setCrewScheduleCode] = useState('');
  const [crewScheduleCodes, setCrewScheduleCodes] = useState([]);
  
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({ isActive: 'ACTIVE' }));
  const defaultFilters = {
    isActive: 'ACTIVE',
  };
  
  const location = useLocation();
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!restoredRef.current && location.state?.filters) {
      setFilters(location.state.filters);
      restoredRef.current = true;
    }
  }, [location.state]);


  const fetchCrewScheduleCodes = useCallback(() => {
    return api.get('/crewschedulecodes');
  }, []);

  const fetchCrewMembers = useCallback(() => {
    return api.get('/crewmembers');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);

        const [               
          crewScheduleCodesResponse, 
          crewMembersResponse,
        ] = await Promise.all([
          fetchCrewScheduleCodes(), 
          fetchCrewMembers(),
        ]);
        
        setCrewScheduleCodes(crewScheduleCodesResponse.data);
        
        const processedCrewMembers = crewMembersResponse.data?.map(crewMember => {
          const contactTel = crewMember['contactTel'] || crewMember['contactCell'];
          return {
            ...crewMember,
            contactTel,
          };
        });

        const sortedCrewMembers = sortMultipleFields(
          processedCrewMembers, 
          [ 
            { field: 'crewCode', direction: 'asc' } 
          ], 
          true,
        );

        setCrewMembers(sortedCrewMembers);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch data!");
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchCrewScheduleCodes, 
    fetchCrewMembers,
  ]);   
    
  const filteredCrewMembers = useMemo(() => {
    if (!Array.isArray(crewMembers)) {
      return []; 
    }
    
    if (!filters) return crewMembers;

    return crewMembers.filter((crewMember) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        
        if (key === 'crewName' || key === 'quals' || key === 'email') {
          return crewMember[key]?.toLowerCase()?.includes(value?.toLowerCase()); 
        }

        if (key === 'birthday') {
          const birthDate = crewMember[key] ? format(new Date(crewMember[key]), 'yyyy-MM-dd') : null;
          const formattedValue = value ? format(new Date(value), 'yyyy-MM-dd') : null;
          return birthDate === formattedValue;
        }

        if (key === 'gender') {
          if (value === 'ALL') {
            return true
          } else {
            return crewMember[key] === value;
          };
        }

        if (key === 'location' || key === 'rank') {
          return Array.isArray(value) ? value.includes(crewMember[key]) : crewMember[key] === value;
        }

        if (key === 'trainingQuals') {
          return Array.isArray(value)
            ? value.some(v => (crewMember.trainingQuals || []).includes(v))
            : (crewMember.trainingQuals || []).includes(value);
        }

        if (key === 'isActive') {
          if (value === 'ALL') return true; 
          if (value === 'ACTIVE') return crewMember[key] === true; 
          if (value === 'INACTIVE') return crewMember[key] === false; 
        }

        return crewMember[key] === value;
      });
    });
  }, [crewMembers, filters]);  

  const handleCreate = useCallback(async () => {
    try {
      const url = '/crewmembers/bulk-create';
      const payload = { startDate, endDate };
      const res = await api.post(url, payload);

      if (res.data?.success) {
        if (Array.isArray(res.data.data)) {
          setCrewMembers(res.data.data);
        }
        toast.success(res.data.message ||
          `Crew members inserted: ${res.data.inserted}, updated: ${res.data.modified}.`
        );
      } else {
        toast.error("Bulk create did not succeed.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Failed to insert crew members!");
    }
  }, [startDate, endDate]);

  const handleUpdateRank = useCallback(async () => {
    try {
      const url = '/crewmembers/updateallcrewrank';
      const payload = { startDate, endDate };      
      const res = await api.put(url, payload);

      if (res.data.success) {
        toast.success(res.data.message || "Crew members rank updated successfully.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update crew members rank!");
    } 
  }, [startDate, endDate]); 

  const handleCheckErrors = useCallback(() => {
    try {
      const checks = [];

      crewMembers.forEach(({ 
        crewCode, 
        crewName, 
        birthday,          
        gender,          
        email, 
        contactTel,
        rank,
        address, 
        passportName, 
        passportNumber, 
        passportIssueDate, 
        passportExpiryDate,
        passportCountry,
        isActive, 
      }) => {
        if (!isActive) return;

        const missingFields = [
          { field: crewName, label: "crew name" },
          { field: birthday, label: "birthday" },
          { field: gender, label: "gender" },          
          { field: email, label: "email" },
          { field: contactTel, label: "phone number" },
          { field: rank, label: "rank" },
          { field: address, label: "address" },
          { field: passportName, label: "passport name" },
          { field: passportNumber, label: "passport number" },
          { field: passportExpiryDate, label: "passport expiry date" },
          { field: passportCountry, label: "passport country" },
        ];

        missingFields.forEach(({ field, label }) => {
          if (!field || (typeof field === 'string' && field.trim() === '')) {
            checks.push({ 
              crewCode, 
              crewName, 
              error: `Missing ${label}` 
            });
          }
        });

        if (birthday) {
          const birthDate = birthday ? new Date(birthday) : null;
          if (differenceInYears(Date(), birthDate) < 18) {
            checks.push({
              crewCode,
              crewName,
              birthday: format(birthday, 'dd/MM/yyyy'),
              error: "Crew age < 18 years old",
            });
          }
        }

        const issueDate = passportIssueDate ? new Date(passportIssueDate) : null;
        const expiryDate = passportExpiryDate ? new Date(passportExpiryDate) : null;

        if (issueDate && expiryDate) {
          if (issueDate > expiryDate) {
            checks.push({
              crewCode,
              crewName,
              issueDate: format(issueDate, 'dd/MM/yyyy'),
              expiryDate: format(expiryDate, 'dd/MM/yyyy'),
              error: "Passport issue date is after expiry date",
            });
          }

          if (issueDate.getDate() !== expiryDate.getDate() || issueDate.getMonth() !== expiryDate.getMonth()) {
            checks.push({
              crewCode,
              crewName,
              issueDate: format(issueDate, 'dd/MM/yyyy'),
              expiryDate: format(expiryDate, 'dd/MM/yyyy'),
              error: "Passport issue and expiry date have different day or month",
            });
          }

          if (differenceInMonths(expiryDate, Date()) < 9) {
            checks.push({
              crewCode,
              crewName,
              expiryDate: format(expiryDate, 'dd/MM/yyyy'),
              error: "Passport expiry date < 9 months",
            });
          }
        }
      });

      if (checks.length > 0) {
        setChecksList(checks);
        setShowCheckResultDialog(true);
      } else {
        toast.success("No errors found.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to check errors!");
    }
  }, [crewMembers]);

  const handleExportBirthday = useCallback(async () => {
    try {
      if (!filteredCrewMembers.length) return;
      if (!crewScheduleCode) {
        toast.error("No crew schedule code selected!");
        return;
      }

      let output = "";

      for (const member of filteredCrewMembers) {
        if (!member.birthday) continue;

        const birthDate = new Date(member.birthday);
        const crewCode = member.crewCode || "";
       
        for (let d = new Date(startDate); d <= endDate; d = addDays(d, 1)) {
          if (
            birthDate.getMonth() === d.getMonth() &&
            birthDate.getDate() === d.getDate()
          ) {
            for (let offset = prevQuantity; offset <= postQuantity; offset++) {
              const targetDate = addDays(d, offset);
            
              if (targetDate >= startDate && targetDate <= endDate) {
                output += `${crewCode},${format(targetDate, "ddMMyyyy")},${crewScheduleCode}\n`;
              }
            }
          }
        }
      }

      if (!output) {
        toast.error("No data in the selected period!");
        return;
      }

      const blob = new Blob([output], { type: "text/plain;charset=utf-8" });
      const fileName = `Job5_${format(startDate, "yyyyMMdd")}_${format(endDate, "yyyyMMdd")}.txt`;
      saveAs(blob, fileName);

      toast.success("Export file successfully.");
    } catch (error) {
      toast.error(error?.message || "Failed to export file!");
    }
  }, [
    startDate,
    endDate,
    prevQuantity,
    postQuantity,
    crewScheduleCode,
    filteredCrewMembers,
  ]);

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const handleViewClick = useCallback(async (param) => {
    if (!startDate || !endDate) return;

    const crewCode = param.crewCode || '';
    const crewName = param.crewName || '';
    setSelectedCrewCode(crewCode);
    setSelectedCrewName(crewName);
    let result = [];

    try {
      result = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);       
      setSelectedCrewRosters(result);
      setOpenRosterDetailsDialog(true);
      return;
    } catch {      
      setOpenRosterDetailsDialog(false);      
    }

    try {
      result = await fetchGroupedLegMembersByCrewCode(crewCode, startDate, endDate);
      setSelectedCrewRosters(result);
      setOpenLegMembersDialog(true);
      return;
    } catch (error) {      
      setOpenLegMembersDialog(false);
    } 
  }, [startDate, endDate]);
  
  const handleCloseRosterDetailsDialog = () => {
    setSelectedCrewRosters([]);
    setOpenRosterDetailsDialog(false);
  };
  
  const handleCloseLegMembersDialog = () => {
    setSelectedCrewRosters([]);
    setOpenLegMembersDialog(false);
  };

  const handleOpenDependents = useCallback((crew) => {
    setSelectedCrew(crew);
    setOpenDependentsDialog(true);
  }, []);

  const handleSaveDependents = async (newDependents) => {
    try {
      const dateFields = [                
        'birthDate',
        'validSince', 
        'validUntil',
      ];

      const dependents = newDependents.map((dependent) => {
        const converted = { ...dependent };
        dateFields.forEach((field) => {
          converted[field] = dependent[field]
            ? parseUTCDate(dependent[field])
            : null;
        });
        return converted;
      });
      
      const url = `/crewmembers/${selectedCrew._id}/dependents`;
      const payload = { dependents };
      await api.put(url, payload);

      // Cập nhật lại dữ liệu local
      setCrewMembers((prev) =>
        prev.map((crew) =>
          crew._id === selectedCrew._id
            ? { ...crew, dependents }
            : crew
        )
      );

      // Cập nhật selectedCrew hiện tại (nếu dialog còn dùng)
      setSelectedCrew((prev) => ({ ...prev, dependents }));
      
      toast.success("Dependents updated successfully.");
      setOpenDependentsDialog(false);
    } catch (err) {
      toast.error(err?.message || "Failed to update dependents.");
    }
  }; 

  const dateFormatter = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return isNaN(date) ? '' : format(date, 'dd/MM/yyyy');
  };
  
  const columns = useMemo(() => [    
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    { 
      field: "crewCode", 
      headerName: "Code", 
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "crewName",
      headerName: "CrewName",
      cellClassName: "name-column--cell",
      ...isMobile ? { width: 280 } : { flex: 4 },
      renderCell: (params) => {
        const gender = params.row.gender; 
        const color = gender === 'FM' 
          ? theme.palette.secondary.main 
          : theme.palette.primary.dark;

        return (
          <Box sx={{ color }}>
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "shortName",
      headerName: "ShortName",    
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },    
    {
      field: "gender",
      headerName: "Gender",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.1 };
      })(),
    },
    {
      field: "birthday",
      headerName: "Birthday",
      valueFormatter: (value) => dateFormatter(value),
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    }, 
    {
      field: "marital",
      headerName: "Marital",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
    {
      field: "jobTitles",
      headerName: "JobTitles",
      width: 222,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.jobTitles || [];
        if (!list.length) return "";

        return (
          <Box
            sx={{
              height: '100%',
              display: "flex",
              flexDirection: "column",
              overflowY: 'auto',
              overflowX: 'hidden',
              gap: 0.5,
              pr: 1,
            }}
          >
            {list.map((item, index) => (
              <Box
                key={index}
                display="flex"
                gridTemplateColumns="120px 1fr auto"
                alignItems="center"
                columnGap={1}
                
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "primary.main",
                    whiteSpace: "normal",     // cho phép xuống dòng
                    wordBreak: "break-word",  // tự ngắt từ
                    overflowWrap: "anywhere"  // fallback 
                  }}
                >
                  {Array.isArray(item.jobTitle)
                    ? item.jobTitle.map((jt, i) => (
                        <React.Fragment key={i}>
                          {jt}
                          <br />
                        </React.Fragment>
                      ))
                    : item.jobTitle}
                </Typography>

                <Typography variant="body2" sx={{ textAlign: "right" }}>
                  {item.effectiveFrom && format(item.effectiveFrom, 'dd/MM/yyyy')}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: "right" }}>
                  {item.effectiveTo && format(item.effectiveTo, 'dd/MM/yyyy')}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
    {
      field: "rank",
      headerName: "Rank",
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },
    {
      field: "location",
      headerName: "Base",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "trainingQuals",
      headerName: "Trainer",
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    }, 
    {
      field: "quals",
      headerName: "Quals",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.8 };
      })(),
    },         
    {
      field: "division",
      headerName: "Division",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: "department",
      headerName: "Department",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: "deptUnit",
      headerName: "Dept Unit",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: "agency",
      headerName: "Agency",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: "jobGradeCode",
      headerName: "JobGradeCode",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: "salaryGradeCode",
      headerName: "SalaryGradeCode",
      ...isMobile ? { width: 100 } : { flex: 1 },
    },
    {
      field: "employmentStartDate",
      headerName: "EmploymentStartDate",
      ...isMobile ? { width: 120 } : { flex: 2 },
      valueFormatter: (value) => dateFormatter(value),
    },          
    {
      field: "employmentEndDate",
      headerName: "EmploymentEndDate",      
      ...isMobile ? { width: 120 } : { flex: 2 },
      valueFormatter: (value) => dateFormatter(value),
    }, 
    {
      field: "address",
      headerName: "Address",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "city",
      headerName: "City",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "state",
      headerName: "State",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "zip",
      headerName: "Zip",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1 };
      })(),
    },
    {
      field: "contactCell",
      headerName: "ContactCell",
    },
    {
      field: "contactCell2",
      headerName: "ContactCell2",
    },
    {
      field: "contactCell3",
      headerName: "ContactCell3",
    },
    {
      field: "contactTel",
      headerName: "Phone",
      ...isMobile ? { width: 100 } : { flex: 1.8 },
      valueFormatter: (params) => 
        params ? params.replace(/\s+/g, "") : "",
    },
    {
      field: "contactTel2",
      headerName: "ContactTel2",
    },
    {
      field: "contactTel3",
      headerName: "ContactTel3",
    },
    {
      field: "email",
      headerName: "Email",
      ...isMobile ? { width: 200 } : { flex: 3 },
    },
    {
      field: "language1",
      headerName: "Language1",
    },
    {
      field: "language2",
      headerName: "Language2",
    },
    {
      field: "licenceNum",
      headerName: "LicenceNum",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
    {
      field: "employmentDate",
      headerName: "EmploymentDate",
      ...isMobile ? { width: 100 } : { flex: 1.5 },
    },
    {
      field: "nextKinName",
      headerName: "NextKinName",
      ...isMobile ? { width: 250 } : { flex: 3 }
    },
    {
      field: "nextKinRelsn",
      headerName: "NextKinRelsn",
      ...isMobile ? { width: 120 } : { flex: 2 },
    },
    {
      field: "nextKinContact",
      headerName: "NextKinContact",
      ...isMobile ? { width: 120 } : { flex: 2 },
    },    
    {
      field: "passportName",
      headerName: "PassportName",
      ...(() => {
        return isMobile ? { width: 250 } : { flex: 3 };
      })(),
    },
    {
      field: "passportNumber",
      headerName: "PassportNumber",
      ...isMobile ? { width: 120 } : { flex: 2 },
    },    
    {
      field: "passportIssueDate",
      headerName: "PassportIssueDate",      
      valueFormatter: (value) => dateFormatter(value),
      ...(() => {
        return isMobile ? { width: 120 } : { flex: 2 };
      })(),
    },
    {
      field: "passportExpiryDate",
      headerName: "PassportExpiryDate",
      valueFormatter: (value) => dateFormatter(value),
      ...(() => {
        return isMobile ? { width: 120 } : { flex: 2 };
      })(),
    },
    {
      field: "passportCountry",
      headerName: "PassportCountry",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "nationality",
      headerName: "Nationality",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
    {
      field: "insuranceObject",
      headerName: "InsuranceObject",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
    {
      field: "insuranceDayCalculationType",
      headerName: "InsuranceDayCalculationType",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "isNetIncome",
      headerName: "IsNetIncome",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.5 };
      })(),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(() => {
        return isMobile ? { width: 100 } : { flex: 1.3 };
      })(),
    },
    // {
    //   field: "loggedAt",
    //   headerName: "loggedAt",      
    //   valueFormatter: (value) => {
    //     if (value == null) {
    //       return '';
    //     }
    //     return `${format(value, "dd/MM/yyyy")}`;
    //   },
    //   ...(() => {
    //     return isMobile ? { width: 120 } : { flex: 2 };
    //   })(),
    // },
  ], [theme, isMobile]);

  const hiddenColumns = useMemo(() => {  
    return {
      _id: false,
      no: false,
      shortName: false,
      gender: false,
      birthday: false,
      location: false,
      rank: false,
      trainingQuals: false,
      quals: false,
      language1: false,
      language2: false,
      address: false,
      city: false,
      state: false,
      zip: false,
      contactCell: false,
      contactCell2: false,
      contactCell3: false,
      contactTel: false,
      contactTel2: false,
      contactTel3: false,
      employmentStartDate: false,
      employmentEndDate: false,
      division: false,
      department: false,
      deptUnit: false,
      agency: false,
      jobTitles: false,
      jobGradeCode: false,
      salaryGradeCode: false,
      nationality: false,
      marital: false,
      licenceNum: false,
      employmentDate: false,
      passportName: false,
      passportNumber: false,
      passportIssueDate: false,
      passportExpiryDate: false,
      passportCountry: false,
      nextKinName: false,
      nextKinRelsn: false,
      nextKinContact: false,
      insuranceObject: false,
      insuranceDayCalculationType: false,
      isNetIncome: false,
      isActive: false,
    }
  }, []);

  const nonMobileHiddenColumns = useMemo(() => {
    return {
      _id: false,    
      shortName: false,
      trainingQuals: false,
      quals: false,
      language1: false,
      language2: false,
      address: false,
      city: false,
      state: false,
      zip: false,
      contactCell: false,
      contactCell2: false,
      contactCell3: false,
      contactTel: false,
      contactTel2: false,
      contactTel3: false,
      employmentStartDate: false,
      employmentEndDate: false,
      division: false,
      department: false,
      deptUnit: false,
      agency: false,
      jobTitles: false,
      jobGradeCode: false,
      salaryGradeCode: false,
      nationality: false,
      marital: false,
      licenceNum: false,
      employmentDate: false,
      passportName: false,
      passportNumber: false,
      passportIssueDate: false,
      passportExpiryDate: false,
      passportCountry: false,
      nextKinName: false,
      nextKinRelsn: false,
      nextKinContact: false,
      insuranceObject: false,
      insuranceDayCalculationType: false,
      isNetIncome: false,
    }
  }, []);

  const actionColumn = useMemo(() => {    
    const activeCount = [canView, canEdit, canDependents].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <FlexCenter 
            gap={1} 
            sx={{ width: '100%', height: '100%' }}
          >
            {canView && (
              <Tooltip title="View">
                <IconButton
                  aria-label="view"
                  sx={{ color: "primary.dark" }}
                  onClick={() => {
                    if (!canView) return;
                    handleViewClick(params.row);
                  }}
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
            )}

            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{ color: "primary.dark" }}
                  onClick={() => {
                    if (!canEdit) return;
                    navigate(`/crewmembers/${params.row._id}`, {
                      state: { filters }
                    });

                  }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}

            {canDependents && (
              <Tooltip title="Dependents">
                <IconButton
                  aria-label="Dependents"
                  sx={{ color: "primary.dark" }}
                  onClick={() => {
                    if (!canDependents) return;
                    handleOpenDependents(params.row);
                  }}
                >
                  <GroupAdd />
                </IconButton>
              </Tooltip>
            )}
          </FlexCenter>
        ),
      },
    ];
  }, [
    navigate, 
    canView, 
    canEdit,
    canDependents,
    filters,
    handleViewClick, 
    handleOpenDependents,
  ]);

  const {
    genderOptions,
    baseOptions,
    rankOptions,
    trainingQualOptions,
    isActiveOptions
  } = useMemo(() => {
    if (!crewMembers?.length) {
      return {
        genderOptions: [],
        baseOptions: [],
        rankOptions: [],
        trainingQualOptions: [],
        isActiveOptions: []
      };
    }

    const genders = new Set();
    const bases = new Set();
    const ranks = new Set();
    const trainingQuals = new Set();
    const isActives = new Set();

    for (const m of crewMembers) {
      if (m?.gender) genders.add(m.gender);
      if (m?.location) bases.add(m.location);
      if (m?.rank) ranks.add(m.rank);
      if (typeof m?.isActive === 'boolean') isActives.add(m.isActive);

      m?.trainingQuals?.forEach(q => q && trainingQuals.add(q));
    }

    const rankMap = new Map(
      crewRanksData.map(r => [r.rankCode, r])
    );

    const sortedRanks = [...ranks]
      .map(r => rankMap.get(r))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode);

    return {
      genderOptions: [...genders].sort(),
      baseOptions: [...bases].sort(),
      rankOptions: sortedRanks,
      trainingQualOptions: [...trainingQuals].sort(),
      isActiveOptions: [...isActives].sort()
    };
  }, [crewMembers, crewRanksData]);
    
  const columnsToShow = [...columns, ...actionColumn];
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isMobile ? hiddenColumns : nonMobileHiddenColumns
  );

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? hiddenColumns : nonMobileHiddenColumns);
  }, [isMobile, hiddenColumns, nonMobileHiddenColumns]);


  return (
    <Box className="main-container-box">                
      <FlexBetween>
        <Header 
          title="Crew Members" 
          subtitle={loading ? "Loading..." : `List of ${filteredCrewMembers?.length || 0} Members`}
        /> 
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}> 
            {!isMobile && (
              <Box display="flex" alignItems="center" gap={1}>
                {canExport && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <NumberControl
                      min={-31}
                      max={0}
                      textFieldWidth={45}
                      value={prevQuantity}
                      onChange={(value) => { setPrevQuantity(value) }} 
                    />   

                    <FormControl
                      size="small"
                      variant="outlined"
                      sx={{ minWidth: 150 }}
                    >
                      <InputLabel>Schedule Code</InputLabel>
                      <Select                     
                        label="Schedule Code"
                        name="crewScheduleCode"
                        value={crewScheduleCode ?? ""}
                        onChange={(e) => setCrewScheduleCode(e.target.value)}
                      >
                        <MenuItem key='none' value=''>None</MenuItem>
                        {crewScheduleCodes
                          ?.slice() 
                          ?.sort((a, b) => {
                            const catCompare = a.category.localeCompare(b.category);
                            if (catCompare !== 0) return catCompare; 
                            return a.code.localeCompare(b.code);
                          })
                          ?.map((item, index) => (                                    
                            <MenuItem key={item._id || index} value={item.code}>
                              {item.code + ' - ' + item.description}
                            </MenuItem>
                          ))
                        }
                      </Select>
                    </FormControl>

                    <NumberControl
                      min={0}
                      max={31}
                      textFieldWidth={45}
                      value={postQuantity}
                      onChange={(value) => { setPostQuantity(value) }} 
                    />    
                    
                    <Tooltip title="Export birthday leaves to Job #5" arrow>
                      <span>
                        <StyledButton 
                          onClick={() => {
                            if (!canExport) return;
                            handleExportBirthday();
                          }}
                        >
                          <IosShare />
                        </StyledButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                
                {canCreate && (
                  <Tooltip title="Create crew from leg members" arrow>
                    <span>
                      <StyledButton 
                        onClick={() => {
                          if (!canCreate) return;
                          handleCreate();
                        }}
                      >
                        <Add />
                      </StyledButton>
                    </span>
                  </Tooltip>
                )}

                {canUpdateRank && (
                  <Tooltip title="Update crew rank" arrow>
                    <span>
                      <StyledButton 
                        onClick={() => {
                          if (!canUpdateRank) return;
                          handleUpdateRank();
                        }}
                      >
                        <Update />
                      </StyledButton>
                    </span>
                  </Tooltip>
                )}
              </Box>  
            )}
                
            <Tooltip title="Check errors" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canCheck) return;
                    handleCheckErrors();
                  }}
                >
                  <Warning />
                </StyledButton>
              </span>
            </Tooltip>

            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />  
          </Box>
        )}                
      </FlexBetween>            
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
        >
          <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
          >                            
            {isMobile ? (
              <CrewMemberList
                rows={filteredCrewMembers}
                onView={handleViewClick}
                onEdit={(member) => navigate(`/crewmembers/${member._id}`)}
                onDependents={handleOpenDependents}
              />
            ) : (
              <DataGridComponent
                rows={filteredCrewMembers}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  sx: {
                    "& .MuiDataGrid-row.isActive-false": {
                      backgroundColor: `${theme.palette.primary.light} !important`,  
                    },
                  },                  
                  getRowClassName: ({ row }) => {
                    if (row.isActive === false) return 'isActive-false';
                    return '';
                  },
                }}
              />
            )}

            <ScrollToTop scrollRef={scrollRef} />
          </Box>    
          
          {showCheckResultDialog && (
            <CheckResultDialog
              open={showCheckResultDialog}
              onClose={() => setShowCheckResultDialog(false)}
              title="Checks"
              checksList={checksList}
              descriptionKey="error"
            />          
          )}

          {isFilterDialogOpen &&
            <CrewFilterDialog
              open={isFilterDialogOpen}
              onClose={handleFilterClose}
              onApply={handleFilterApply}
              genderOptions={genderOptions}
              baseOptions={baseOptions}
              rankOptions={rankOptions}
              trainingQualOptions={trainingQualOptions}
              isActiveOptions={isActiveOptions}
              currentFilters={filters}
            />
          }
          
          {openRosterDetailsDialog &&
            <RosterDetailsDialog 
              open={openRosterDetailsDialog}
              onClose={handleCloseRosterDetailsDialog}
              crewCode={selectedCrewCode}
              crewName={selectedCrewName}
              crewRosters={selectedCrewRosters}
            />
          }
          {openLegMembersDialog &&
            <LegMembersDialog 
              open={openLegMembersDialog}
              onClose={handleCloseLegMembersDialog}
              crewCode={selectedCrewCode}
              crewName={selectedCrewName}
              crewLegs={selectedCrewRosters}
            />
          }

          {openDependentsDialog && (
            <DependentsDialog
              open={openDependentsDialog}
              onClose={() => setOpenDependentsDialog(false)}
              selectedCrew={selectedCrew || {}}
              onSave={handleSaveDependents}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CrewMembers;
